import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import config from './config';
import Header from './components/Header';
import apiService from './services/apiService';

function SimilarCandidatesPage() {
  const { candidateId } = useParams();
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedCandidate, setSelectedCandidate] = useState(null);
  const navigate = useNavigate();

  // For heading: get the details of the reference candidate
  const [referenceCandidate, setReferenceCandidate] = useState(null);

  useEffect(() => {
    async function fetchSimilar() {
      setLoading(true);
      try {
        const data = await apiService.getSimilarCandidates(candidateId, 20);
        setResults(data);

        // Fetch the reference candidate's details for the heading
        try {
          const refData = await apiService.getCandidateDetails(candidateId);
          setReferenceCandidate(refData);
        } catch (refError) {
          setReferenceCandidate({ id: candidateId, name: 'Unknown Candidate' });
        }
      } catch (err) {
        console.error('Error fetching similar candidates:', err);
        setReferenceCandidate({ id: candidateId, name: 'Unknown Candidate' });
      } finally {
        setLoading(false);
      }
    }
    fetchSimilar();
  }, [candidateId]);

  // Modal close handler
  const closeModal = () => setSelectedCandidate(null);

  return (
    <div className="App">
      <div className="container">
        <Header showBackButton={true} onBackClick={() => navigate(-1)} />
        
        {/* Reference Candidate Details Section */}
        {referenceCandidate && (
          <div className="mandate-section" style={{ background: "#f8f9fa", borderRadius: 12, padding: 24, marginBottom: 24 }}>
            <h2 style={{ marginBottom: 16 }}>👤 Finding Similar Candidates for</h2>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 24, width: "100%" }}>
              <div><strong>ID:</strong> {referenceCandidate.id}</div>
              <div><strong>Name:</strong> {referenceCandidate.name || 'Not specified'}</div>
              <div><strong>Location:</strong> {referenceCandidate.location || 'Not specified'}</div>
              <div><strong>CTC:</strong> {referenceCandidate.ctc ? `${referenceCandidate.ctc} LPA` : 'Not specified'}</div>
              <div><strong>Experience:</strong> {referenceCandidate.experience ? `${referenceCandidate.experience} years` : 'Not specified'}</div>
              <div style={{ width: "100%" }}><strong>Key Skills:</strong> {referenceCandidate.key_skills || 'Not specified'}</div>
              {referenceCandidate.work_summary && (
                <div style={{ width: "100%" }}>
                  <strong>Summary:</strong>
                  <div style={{ background: "#fff", borderRadius: 8, padding: 12, marginTop: 4 }}>
                    {referenceCandidate.work_summary}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {loading && <div>Loading...</div>}
        {results && (
          <div className="results-section">
            <div className="results-header">
              <h2>🎯 Similar Candidates</h2>
              <div className="results-stats">
                <span className="stat">📈 {results.results?.length || 0} similar candidates found</span>
              </div>
            </div>
            <div className="candidates-table-wrapper">
              <table className="candidates-table">
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>Name</th>
                    <th>Match %</th>
                    <th>Location</th>
                    <th>CTC (LPA)</th>
                    <th>Experience (Years)</th>
                  </tr>
                </thead>
                <tbody>
                  {results.results.map((candidate) => (
                    <tr
                      key={candidate.id}
                      className="candidate-row"
                      onClick={() => setSelectedCandidate(candidate)}
                      style={{ cursor: 'pointer' }}
                      tabIndex={0}
                      aria-label={`View details for ${candidate.name || candidate.id}`}
                    >
                      <td>{candidate.id}</td>
                      <td>{candidate.name || '-'}</td>
                      <td>{(candidate.score * 100).toFixed(1)}%</td>
                      <td>{candidate.location || '-'}</td>
                      <td>{candidate.ctc ? `${candidate.ctc} LPA` : '-'}</td>
                      <td>{candidate.experience ? `${candidate.experience} years` : '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            
            {/* Modal for candidate details */}
            {selectedCandidate && (
              <div className="modal-overlay" onClick={closeModal}>
                <div
                  className="modal-content"
                  onClick={e => e.stopPropagation()}
                  tabIndex={-1}
                >
                  <button className="modal-close" onClick={closeModal} aria-label="Close">&times;</button>
                  <h2>
                    {selectedCandidate.name ? (
                      <>
                        <span className="modal-name">{selectedCandidate.name}</span>
                        <span className="modal-id"> (ID: <a 
                          href={`${config.EXTERNAL_CANDIDATE_BASE_URL}${selectedCandidate.id}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{
                            color: '#007bff',
                            textDecoration: 'underline',
                            cursor: 'pointer'
                          }}
                          title="Open candidate in external application"
                        >
                          {selectedCandidate.id}
                        </a>)</span>
                      </>
                    ) : (
                      <>ID: <a 
                        href={`${config.EXTERNAL_CANDIDATE_BASE_URL}${selectedCandidate.id}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{
                          color: '#007bff',
                          textDecoration: 'underline',
                          cursor: 'pointer'
                        }}
                        title="Open candidate in external application"
                      >
                        {selectedCandidate.id}
                      </a></>
                    )}
                  </h2>
                  <div className="modal-fields">
                    <div className="field-row">
                      <span className="label">📍 Location</span>
                      <span className="value">{selectedCandidate.location || 'Not specified'}</span>
                    </div>
                    <div className="field-row">
                      <span className="label">💰 CTC</span>
                      <span className="value">{selectedCandidate.ctc ? `${selectedCandidate.ctc} LPA` : 'Not specified'}</span>
                    </div>
                    <div className="field-row">
                      <span className="label">🧑‍💼 Experience</span>
                      <span className="value">{selectedCandidate.experience ? `${selectedCandidate.experience} years` : 'Not specified'}</span>
                    </div>
                    <div className="field-row">
                      <span className="label">🛠️ Key Skills</span>
                      <span className="value">{selectedCandidate.key_skills || 'Not specified'}</span>
                    </div>
                    {selectedCandidate.other_skills && (
                      <div className="field-row">
                        <span className="label">🎯 Other Skills</span>
                        <span className="value">{selectedCandidate.other_skills}</span>
                      </div>
                    )}
                    {selectedCandidate.work_summary && (
                      <div className="field-row">
                        <span className="label">📝 Summary</span>
                        <span className="value">{selectedCandidate.work_summary}</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default SimilarCandidatesPage;